import { useState } from "react";
import { Button } from "components/atoms/Button";
import { TextIcon } from "components/atoms/TextIcon";
import { useNavigate } from "react-router-dom";
import { decodeToken } from "utils/functions/decodeToken";
import { TextOptionPagination } from "../TextOptionPagination";
import { DASHBOARD_PROGRAME, DESKTOP_WIDTH, MCAT_TEST_TYPE, SELF_PACED_PROGRAME } from "utils/constants";
import { useSegmentReFetch } from "context";
import { DrillModal } from "components/organisms/TabClassHomeWork";
import { RefreshModal } from "../RefreshModal";
import { replaceSelfPacedArrayOfObjects, reStructureSelfPacedSegment } from "utils/functions/selfPacedCourseWorkRestructure";
import { replaceArrayOfObjects, reStructureSegment } from "utils/functions/courseWorkReStructure";
import { useGetCourseworkQuery, useGetOverallProgressQuery, useGetTestScoresAndPerformanceQuery } from "services/homeApi";
import { useLazyGetStudyPlannerEventsQuery } from "services/studyPlanner";
import { useLazyGetCourseIndexQuery } from "services/courseIndex";
import { useLazyGetAiRecommendationsQuery } from "services/aiRecommendations";
import { getObjectKeysByValue } from "services/helpers";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import { useAppSelector, useAppDispatch, useScreenSize } from "hooks";
import { ai_mcat_chat_status } from "state/reducers/layoutSlice";
import { openExternalUrl } from "utils/functions/openExternalUrl";
import "./index.scss";

interface IProps {
  isMobile?: boolean;
  exams?: any;
  textIconStyle?: string;
  title?: string;
  theme: "light" | "dark";
}

export const CourseIndexTextOptionBtn = ({
  isMobile,
  exams,
  title,
  textIconStyle,
  theme,
}: IProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const payload = decodeToken();

  const screen = useScreenSize();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const { data, setData, selectedBtn, selectedSession, subject } = useSegmentReFetch();

  const isDashboard =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === DASHBOARD_PROGRAME;

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const testtype = payload &&
    payload.hasOwnProperty("testtype") && payload.testtype;

  const [courseworkPage, setCourseWorkPage] = useState(1);

  const [openUpdateModalState, setOpenUpdateModalState] = useState<any>({
    isOpen: false,
    data: null,
  });

  const [refreshModalStatus, setRefreshModalStatus] = useState({
    isOpen: false,
    contentObjectType: "",
  });

  const { aiMCATChatDrawerStatus } = useAppSelector(
    (state: { layout: { aiMCATChatDrawerStatus: ai_mcat_chat_status } }) =>
      state.layout
  );

  let hasSmallScreenWithChatDrawerOpen =
    screen.width > DESKTOP_WIDTH &&
    screen.width < 1600 &&
    aiMCATChatDrawerStatus === "open";

  const {
    refetch: overAllProgressRefetch,
    isFetching: overAllProgressIsFetching,
  } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const [ triggerStudyPlannerRefetch, studyPlannerRefetchResult ] =
    useLazyGetStudyPlannerEventsQuery();

  const [ triggerCourseIndexRefetch, refetchCourseIndexIsResult ] =
  useLazyGetCourseIndexQuery();

  const { refetch: testScoresRefetch, isFetching: testScoresIsFetching } =
    useGetTestScoresAndPerformanceQuery();

  const [
    triggerRefetchAiRecommendation,
    aiRecommendationRefetchResult,
  ] = useLazyGetAiRecommendationsQuery();

  const { refetch: courseWorkRefetch, isFetching: courseWorkIsFetching } =
    useGetCourseworkQuery({
      page: courseworkPage,
      ...(isDashboard
        ? {
            selectedOption: parseInt(
              getObjectKeysByValue(selectedBtn, true)[0],
              10
            ),
          }
        : {}),
      ...(isDashboard
        ? {
            timeFilter:
              selectedSession === null
                ? 1
                : getTimeFilterInput(Object.values(selectedSession)[0]),
          }
        : {}),
      ...(isSelfPaced && testtype === MCAT_TEST_TYPE ? {
        subject: subject
      } : {})
    });

  const handleModalClose = () => {
    setRefreshModalStatus((prev) => ({
      ...prev,
      isOpen: false,
      contentObjectType: "",
    }));
  };

  const handleUpdateModalClose = () => {
    setOpenUpdateModalState((prev: any) => ({
      ...prev,
      isOpen: false,
      data: null,
    }));
  };

  const matchBtnLabel = (status: any) => {
    switch (status) {
      case "Not Started":
        return "Start Diagnostic Exam";
      case "Inprogress":
        return "Resume";
      case "Complete":
        return "Restart Diagnostic Exam";
      default:
        return "Start Diagnostic Exam";
    }
  };

  const handleSetCourseworkPage = () => {
    let selectedSegment: any = undefined;
    if (isSelfPaced) {
      selectedSegment =
        data &&
        Array.isArray(data) &&
        data.filter((item) => item.LearningTrackName === title);
    } else {
      selectedSegment =
        data &&
        Array.isArray(data) &&
        data.filter((item) => item.Description === title);
    }
    if (
      selectedSegment &&
      Array.isArray(selectedSegment) &&
      selectedSegment[0] &&
      selectedSegment[0].page
    ) {
      setCourseWorkPage((prev) => selectedSegment[0].page);
    }
  };

  const handleRefreshCourseIndex = async () => {
    const promises = [
      courseWorkRefetch(),
      triggerCourseIndexRefetch(),
      overAllProgressRefetch(),
      testScoresRefetch(),
      triggerRefetchAiRecommendation(),
      ...(isDashboard ? [triggerStudyPlannerRefetch()] : []),
    ];

    Promise.allSettled(promises).then((responses: any) => {
      if (responses[0].status === "fulfilled") {
        if (isSelfPaced) {
          const deStructuredArray = reStructureSelfPacedSegment(
            responses[0].value.data
          );
          const updatedArray = replaceSelfPacedArrayOfObjects(
            deStructuredArray,
            data
          );
          setData([...updatedArray]);
        } else {
          const deStructuredArray = reStructureSegment(responses[0].value.data);
          const updatedArray = replaceArrayOfObjects(deStructuredArray, data);
          setData([...updatedArray]);
        }
      }
      handleModalClose();
    });
  };

  const handleClick = (course: any) => {
    if (course.Status === "Lock" || course.IsLocked === true) {
      if (isSelfPaced) {
        navigate("/self-paced/unlock");
      }
    } else if (
      course.ContentObjectType === "OfflineDrill" ||
      course.ContentObjectType === "OfflineReading"
    ) {
      if (hasSmallScreenWithChatDrawerOpen) {
        handleSetCourseworkPage();
        setOpenUpdateModalState((prev: any) => ({
          ...prev,
          isOpen: true,
          data: course,
        }));
      } else if (isDashboard) {
        navigate("/drill", { state: course });
      } else if (isSelfPaced) {
        navigate("/self-paced/drill", { state: course });
      }
    } else {
      const testtype = payload?.testtype;
      if(hasSmallScreenWithChatDrawerOpen) {
        handleSetCourseworkPage();
        openExternalUrl({
          url: course.LaunchUrl,
          contentObjectType: course.ContentObjectType,
          testType: testtype,
          dispatch,
          onRefreshModalOpen: (contentType) => {
            setRefreshModalStatus((prev) => ({
              ...prev,
              isOpen: true,
              contentObjectType: contentType || "Test",
            }));
          },
        });
      } else if (course.LaunchUrl !== null) {
        openExternalUrl({
          url: course.LaunchUrl,
          contentObjectType: course.ContentObjectType,
          testType: testtype,
          dispatch,
          onRefreshModalOpen: (contentType) => {
            setRefreshModalStatus((prev) => ({
              ...prev,
              isOpen: true,
              contentObjectType: contentType || "Test",
            }));
          },
        });
      }
    }
  };

  return (
    <>
      {exams && Array.isArray(exams) && exams.length > 5 ? (
        <TextOptionPagination
          exams={exams}
          theme={theme}
          textIconStyle={textIconStyle}
          handleClick={handleClick}
        />
      ) : (
        exams &&
        Array.isArray(exams) &&
        exams.length > 0 &&
        exams.map((value: any, index: number) => (
          <div className="text-option-btn-wrapper" key={index}>
            <div
              className="text-option-section"
              onClick={() => handleClick(value)}
            >
              <TextIcon
                label={value.Name}
                icon={true}
                isReverse={true}
                iconType={
                  value.Status === "Lock" || value.IsLocked === true
                    ? "lock"
                    : value.Status === "Not Started"
                      ? "empty-circle"
                      : value.Status === "Completed" ||
                          value.Status === "Complete"
                        ? "select"
                        : "yellow-timer"
                }
                isTabStyle={true}
                styles={textIconStyle}
                theme={theme}
              />
            </div>

            {isMobile ? null : (
              <div className="btn-section">
                {value.Status === "Completed" || value.Status === "Complete" ? (
                  <Button
                    type="online"
                    label="View Result"
                    icon={false}
                    styles="custom-text-option-btn"
                    onClick={() => {
                      // View Result button should not show refresh modal
                      window.open(value.LaunchUrl, "__blank");
                    }}
                  />
                ) : null}
                <Button
                  type="score"
                  label={matchBtnLabel(value.Status)}
                  icon={false}
                  styles="custom-text-option-btn"
                  onClick={() => window.open(value.LaunchUrl, "__blank")}
                />
              </div>
            )}
          </div>
        ))
      )}

      <RefreshModal
        handleOnClose={handleModalClose}
        contentObjectType={refreshModalStatus.contentObjectType}
        isOpen={refreshModalStatus.isOpen}
        theme={theme}
        handleRefresh={handleRefreshCourseIndex}
        isFetching={
          refetchCourseIndexIsResult.isFetching ||
          courseWorkIsFetching ||
          overAllProgressIsFetching ||
          studyPlannerRefetchResult.isFetching ||
          testScoresIsFetching ||
          aiRecommendationRefetchResult.isFetching
        }
      />
      
      <DrillModal
        handleUpdateModalClose={handleUpdateModalClose}
        openUpdateModalState={openUpdateModalState}
        refetchData={handleRefreshCourseIndex}
        theme={theme}
        isFetchingOtherAPIs={
          refetchCourseIndexIsResult.isFetching ||
          courseWorkIsFetching ||
          overAllProgressIsFetching ||
          studyPlannerRefetchResult.isFetching ||
          testScoresIsFetching ||
          aiRecommendationRefetchResult.isFetching
        }
      />
    </>
  );
};
